// externals
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';

// internals
import { getDealerships } from '@makemydeal/dr-dash-store/selectors/dealershipsSelectors';
import { useDealershipSelector } from '@makemydeal/dr-activities-dealership-picker';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import { useRecentDealsDealershipInfo } from './useRecentDealsDealershipInfo';
import type { SelectedDealership } from '../types';

/**
 * Hook responsible for managing dealership selection state.
 * Follows Single Responsibility Principle - only handles selection state and change handling.
 *
 * Data source resolution (session storage vs Redux) is delegated to useDealershipInfo.
 *
 * @param refetchDeals - Injected callback to trigger data refetch on dealership change (DI pattern).
 *                       Provided by the orchestrator hook (useRecentDeals) to keep fetch logic decoupled.
 * @returns Dealership selection state and handlers
 */
export const useDealershipSelection = (refetchDeals?: (coOrgId: string, mmdId: string) => void) => {
    const dealerships = useSelector(getDealerships);
    const { requestStatus: dealershipsRequestStatus } = useDealershipSelector();
    const { getDealerInfo, setDealerInfo } = useRecentDealsDealershipInfo();

    const [selectedDealership, setSelectedDealership] = useState<SelectedDealership | undefined>();
    const mmdId = getDealerInfo()?.mmdId;

    // Sync selectedDealership with mmdId when it changes (e.g., when navigating from Open Deal to MV)
    useEffect(() => {
        setSelectedDealership(getDealerInfo());
    }, [mmdId]);

    // Check if dealerships are still loading
    const isDealershipsLoading =
        dealershipsRequestStatus === ApiRequestStatus.Idle || dealershipsRequestStatus === ApiRequestStatus.Loading;

    /**
     * Handles dealership change from the DealershipPicker.
     * Looks up the dealership data, updates state and persists via useDealershipInfo,
     * and invokes the injected refetchDeals callback.
     *
     * @param event - Change event from DealershipPicker with target.value containing mmdId
     */
    const handleChangeDealership = useCallback(
        (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
            const mmdId = event.target.value;
            if (mmdId && mmdId !== selectedDealership?.mmdId) {
                // Find the selected dealership data to get coOrgId
                const dealershipData = dealerships.find((data) => `${data.dealerMMDId}` === mmdId);
                const coOrgId = dealershipData?.commonOrgId;

                const newSelection: SelectedDealership = { mmdId, coOrgId };
                setSelectedDealership(newSelection);
                setDealerInfo(newSelection);

                // Invoke injected callback if available and coOrgId is present
                if (coOrgId && refetchDeals) {
                    refetchDeals(coOrgId, mmdId);
                }
            }
        },
        [selectedDealership?.mmdId, dealerships, setDealerInfo, refetchDeals]
    );

    return {
        /** Currently selected dealership (mmdId + coOrgId) */
        selectedDealership,
        /** Handler for dealership change events */
        handleChangeDealership,
        /** Number of available dealerships */
        availableDealershipsCount: dealerships.length,
        /** Indicates if dealerships are currently being fetched */
        isDealershipsLoading
    };
};
